import { useEffect, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { createFileRoute } from '@tanstack/react-router'
import { Card } from '@/components/ui/Card'
import { CompanyLogo } from '@/components/ui/CompanyLogo/CompanyLogo'
import { setAccessToken } from '@/utils/auth-token'

export const Route = createFileRoute('/sso-callback')({
  component: SSOCallbackPage,
})

function SSOCallbackPage() {
  const navigate = useNavigate()
  const search = useSearch({ from: '/sso-callback' })
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function handleCallback() {
      // Token is passed in the URL by the backend after successful Google/Apple SSO
      const token = (search as any).token
      const errorMessage = (search as any).error

      if (errorMessage) {
        setError(String(errorMessage))
        setTimeout(() => navigate({ to: '/login' }), 3000)
        return
      }

      if (!token) {
        setError('Authentication failed: No token received from provider.')
        setTimeout(() => navigate({ to: '/login' }), 3000)
        return
      }

      try {
        // 1. Store the token temporarily
        setAccessToken(token)

        // 2. Force a hard redirect to the home page so AuthProvider mounts and auto-fetches the user profile
        window.location.href = '/home'
      } catch (err) {
        console.error('Error handling SSO callback:', err)
        setError('Failed to complete authentication. Please try again.')
        setTimeout(() => navigate({ to: '/login' }), 3000)
      }
    }

    handleCallback()
  }, [search, navigate])

  return (
    <div className="flex h-screen w-full items-center justify-center bg-gray-50/50">
      <Card className="w-full max-w-md shadow-xl">
        <div className="flex flex-col items-center justify-center space-y-6 pt-10 pb-10">
          <CompanyLogo size="lg" className="h-10 w-auto" />

          <div className="flex flex-col items-center space-y-2 text-center">
            {error ? (
              <>
                <div className="rounded-full bg-red-100 p-3 text-red-600 mb-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-gray-900">Authentication Error</h2>
                <p className="text-sm text-gray-500">{error}</p>
                <p className="text-xs text-gray-400 mt-2">Redirecting back to login...</p>
              </>
            ) : (
              <>
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#4F46E5] mb-2" />
                <h2 className="text-xl font-semibold text-gray-900">Completing Sign In</h2>
                <p className="text-sm text-gray-500">Please wait while we securely log you in...</p>
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  )
}
